import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { ArchTags } from '../../components/mmode/archs';
import { Device, deviceLabel } from '../../api/device';
import { useDictText } from '../../components/DictSelect';
import {
    MMDeviceRefSaveItem,
    MMGroup,
    MMMode,
    listMMDeviceRefs,
    listMMGroups,
    listMMModes,
    saveMMDeviceRefs,
} from '../../api/mmode';
import { ApiError, alertApprovalIfPending } from '../../api/request';
import { alertDialog } from '../../utils/dialog';

// ★设计A: 设备侧加工模式引用维护 (主维护口).
// 勾选该设备支持的模式值 + 设备差异 (中文别名 / 速度范围); 整体保存.
// 非 admin 保存转审批工单. 与"模式能力"(line/fill/cut) 是两个维度, 勿混淆.

interface RefDraft extends MMDeviceRefSaveItem {
    checked: boolean;
}

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

function MModeRefModal({ device, onClose }: { device: Device; onClose: () => void }) {
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    const [groups, setGroups] = useState<MMGroup[]>([]);
    const [modes, setModes] = useState<MMMode[]>([]);
    const [drafts, setDrafts] = useState<Record<string, RefDraft>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [gs, ms, refs] = await Promise.all([
                    listMMGroups(),
                    listMMModes(),
                    listMMDeviceRefs(device.deviceKey),
                ]);
                if (cancelled) return;
                setGroups(gs);
                setModes(ms);
                const byValue: Record<string, RefDraft> = {};
                for (const r of refs) {
                    byValue[r.modeValue] = {
                        checked: true,
                        modeValue: r.modeValue,
                        enabled: r.enabled,
                        aliasZh: r.aliasZh,
                        speedMin: r.speedMin,
                        speedMax: r.speedMax,
                        sort: r.sort,
                    };
                }
                setDrafts(byValue);
            } catch (e) {
                if (!cancelled) setError(errMsg(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [device.deviceKey]);

    const grouped = useMemo(() => {
        const byGroup: { group: MMGroup | undefined; code: string; modes: MMMode[] }[] = [];
        const seen = new Set<string>();
        for (const m of modes) {
            if (!seen.has(m.groupCode)) {
                seen.add(m.groupCode);
                byGroup.push({
                    group: groups.find((g) => g.code === m.groupCode),
                    code: m.groupCode,
                    modes: modes.filter((x) => x.groupCode === m.groupCode),
                });
            }
        }
        return byGroup;
    }, [modes, groups]);

    const toggle = (value: string) => {
        setDrafts((prev) => {
            const cur = prev[value];
            if (cur?.checked) {
                return { ...prev, [value]: { ...cur, checked: false } };
            }
            return { ...prev, [value]: { ...(cur || {}), checked: true, modeValue: value } };
        });
    };

    const patch = (value: string, p: Partial<RefDraft>) => {
        setDrafts((prev) => ({ ...prev, [value]: { ...prev[value], ...p } }));
    };

    const onSave = async () => {
        setSaving(true);
        try {
            const refs = Object.values(drafts)
                .filter((d) => d.checked)
                .map(({ checked, ...rest }) => {
                    void checked;
                    return rest;
                });
            const res = await saveMMDeviceRefs(device.deviceKey, refs);
            alertApprovalIfPending(res.message);
            onClose();
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setSaving(false);
        }
    };

    const checkedCnt = Object.values(drafts).filter((d) => d.checked).length;

    return (
        <Modal
            open
            title={`加工模式引用 · ${deviceLabel(device, lightText)}`}
            subtitle="勾选该设备支持的加工模式; 别名/速度是设备级差异, 空 = 用字典默认. 与「模式能力」(矢量/填充/位图/切割) 是不同维度."
            onClose={onClose}
            width={760}
        >
            {error && <div className="text-sm text-rose-600 mb-3">错误: {error}</div>}
            {loading ? (
                <div className="text-center text-slate-400 text-sm py-8">加载中…</div>
            ) : (
                <div className="space-y-4 max-h-[60vh] overflow-auto pr-1">
                    {grouped.map(({ group, code, modes: ms }) => (
                        <div key={code}>
                            <div className="text-xs font-semibold text-slate-500 mb-1.5">
                                {group?.nameZh || code}
                                <span className="ml-1 font-normal inline-flex gap-1 align-middle">
                                    <ArchTags codes={group?.archCodes} />
                                </span>
                                <span className="ml-1 text-slate-300 font-normal">(同组共享一份参数)</span>
                            </div>
                            <div className="space-y-1.5">
                                {ms.map((m) => {
                                    const d = drafts[m.value];
                                    const on = !!d?.checked;
                                    return (
                                        <div
                                            key={m.value}
                                            className={`border rounded-lg px-3 py-2 ${on ? 'border-brand-200 bg-brand-50/40' : 'border-slate-100'}`}
                                        >
                                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                                <input type="checkbox" checked={on} onChange={() => toggle(m.value)} />
                                                <span>{m.nameZh || m.value}</span>
                                                <span className="font-mono text-[11px] text-slate-400">{m.value}</span>
                                            </label>
                                            {on && (
                                                <div className="grid grid-cols-3 gap-2 mt-2 pl-6">
                                                    <input
                                                        className="inp h-8 text-xs"
                                                        placeholder="设备级别名 (空=字典名)"
                                                        value={d?.aliasZh || ''}
                                                        onChange={(e) => patch(m.value, { aliasZh: e.target.value })}
                                                    />
                                                    <input
                                                        className="inp h-8 text-xs"
                                                        type="number"
                                                        placeholder="速度下限"
                                                        value={d?.speedMin ?? ''}
                                                        onChange={(e) =>
                                                            patch(m.value, {
                                                                speedMin: e.target.value === '' ? null : Number(e.target.value),
                                                            })
                                                        }
                                                    />
                                                    <input
                                                        className="inp h-8 text-xs"
                                                        type="number"
                                                        placeholder="速度上限"
                                                        value={d?.speedMax ?? ''}
                                                        onChange={(e) =>
                                                            patch(m.value, {
                                                                speedMax: e.target.value === '' ? null : Number(e.target.value),
                                                            })
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-100">
                <span className="text-xs text-slate-400">已勾选 {checkedCnt} 个模式</span>
                <div className="flex gap-2">
                    <button type="button" className="btn-ghost" onClick={onClose}>
                        取消
                    </button>
                    <button type="button" className="btn-primary" disabled={saving || loading} onClick={onSave}>
                        {saving ? '保存中…' : '保存引用'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default MModeRefModal;
